/*import { Dimens2D, PhraseType } from 'Phaser/Scenes/SightReading';
import PlayablePhrase from '../Containers/PlayablePhrase';
import CursorBase from 'Phaser/GameObjects/CursorBase';
import {
  DefaultConfig,
  MediumConfig,
  MinimumConfig,
  PhraseGraphic,
  PhraseData,
} from 'Phaser/GameObjects/StaffLine';
import { SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import { ResizeParams, optimizeStaffLineHeight } from 'Phaser/GameObjects/StaffLine/Util';
import Logger from 'Utils/Logger';
import LessonCursor from 'Phaser/GameObjects/LessonCursor';
import { EventStreamPhaser2, IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';*/

import CursorMobile, {
  EventStreamPhaser2,
  IteratorExtensible,
} from 'Phaser/GameObjects/CursorMobile';
import {
  DefaultConfig,
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
} from 'Phaser/GameObjects/StaffLine';
import { CursorData, SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import {
  getBraceWidth,
  ResizeParams,
  optimizeStaffLineHeight,
} from 'Phaser/GameObjects/StaffLine/Util';
import { PlayablePhrase as _PlayablePhrase } from 'Phaser/Scenes/SightReadingPhaser';
import {
  SIGHT_READING_MAX_ASPECT_RATIO,
  SIGHT_READING_MIN_ASPECT_RATIO,
  sightReadingDesktopSizing,
} from './sightReadingStaffSizing';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import Logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import Phrase, { Iterator } from 'Models/Phrase';
import { NotifyReactEvents, ReactToPhaserListeners, RemoveListener } from 'Phaser/EventBus2';
import {
  getTransposedMusicXmlKeySignature,
  transformSerializedIteratorPitches,
} from 'Utils/Transposition';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import type { PrerenderedPhraseData } from 'Types';
import { needThreshold, showsNumbers, tierOf, type Join, type Key, type Tables } from 'ErgoBot';
import type { FingeringDisplay, LiveFingering } from 'Fingering/display';
import {
  phraseFingeringReportOf,
  type PhraseFingeringReport,
  type SightReadingFingeringReport,
} from 'Fingering/fingeringReport';
import { handLinesOf } from 'Fingering/handLines';
import { replaceFingering } from 'Fingering/noteFingering';
import { joinOfPhraseNotes } from 'Fingering/phraseJoin';
import { soundingKeyOf } from 'Fingering/soundingKey';
import {
  fingerPhraseNotes,
  NO_HAND_STATES,
  type Clock,
  type HandStates,
  type PhraseFingering,
} from 'Fingering/phraseFingering';
import { getCircularQueueIndices } from './CircularQueue';

export type RequestParams = {
  excludeFirstNoteRest: boolean;
  /** Monotonic id for this request. The producer echoes it back on PhraseReady
   *  so the manager can restore request order for the initial fill. Unique
   *  across every PhraseManager in the page (see {@link allocatePhraseSequenceId}). */
  sequenceId: number;
  /** Where the phrase the requested one will follow leaves each hand, when the
   *  manager fingers phrases and that phrase is known. Phrase selection uses it
   *  to serve a phrase the hands can reach. */
  handStates?: HandStates;
  /** The transposition in force, in semitones, when the manager fingers
   *  phrases: selection judges a phrase by what it will sound as. */
  transposition?: number;
};

/** Next phrase-request sequence id, allocated page-wide rather than per manager.
 *
 *  A cold load of /lesson can re-init the scene while the outgoing manager's
 *  requests are still in flight. Both managers share one EventBus, so a
 *  per-instance counter restarting at 0 put two different phrases on the same
 *  id (MUS-406): the init-fill reorder gate wedged on the duplicate and stalled
 *  playback, and the complexity AssessmentStream — keyed by sequence id — took
 *  its already-graded branch and silently dropped a verdict.
 *
 *  Module scope is deliberate. Scene/manager-scoped state is exactly what the
 *  remount resets, which is the bug. */
let nextPhraseSequenceId = 0;

/** Reserve the next page-unique phrase-request sequence id. */
function allocatePhraseSequenceId(): number {
  return nextPhraseSequenceId++;
}

/** The id {@link allocatePhraseSequenceId} will hand out next, without
 *  reserving it. Lets the init fill name the first id of the contiguous run it
 *  is about to allocate. */
function peekNextPhraseSequenceId(): number {
  return nextPhraseSequenceId;
}

/** Test-only: rewind the page-wide allocator so specs start from a known id. */
export function __resetPhraseSequenceIdsForTest(): void {
  nextPhraseSequenceId = 0;
}

interface LayoutConfig {
  layoutDirection: PhraseLayout;
  minPaddingX: number;
  minPaddingY: number;
}

export enum PhraseLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

/** How many times one opening phrase is asked for again because the hands
 *  cannot get into it smoothly. After that the phrase is taken as it comes. */
const OPENING_JOIN_RETRY_LIMIT = 2;

/** Game pixels between viewport left and first-phrase brace left on mobile
 *  (world distance = this / camera zoom). */
const MOBILE_BRACE_VIEWPORT_GAP_PX = 14;

const defaultConfig: LayoutConfig = {
  layoutDirection: PhraseLayout.Vertical,
  minPaddingX: 30,
  minPaddingY: 10,
};

type PhraseManagerConfig = {
  mobile?: boolean;
  maxPhrasesVisible?: number;
  onPhraseRequested?: (params: RequestParams) => void;
  layoutConfig?: LayoutConfig;
  eventStream?: EventStreamPhaser2;
  // Gameplay callbacks driven by each cursor; supplied by the scene and
  // forwarded to every CursorMobile the manager creates.
  onPlayingLastSlice?: (start: number, duration: number, durationInSecs: number) => void;
  onFinishPlayingPhrase?: (eligible?: boolean) => void;
  onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void;
  // Invoked when a phrase is evicted from the active queue (for cleanup).
  onPhraseRemoved?: (uuid: string) => void;
  // Score-theme colors applied to phrases/cursors the manager creates.
  notationColor?: number;
  staffLineColor?: number;
  cursorColor?: number;
  // Initial sight-reading transposition applied to every phrase the manager
  // builds (updated live via setTranspose).
  transposeSemitones?: number;
  modeConversion?: ParallelModeConversion;
  // Live finger numbers: the tables every phrase is fingered with as it is
  // built, and the density its numbers are drawn at (updated live via
  // setFingeringDisplay). Leave undefined to keep finger numbers out of the
  // manager's hands: each phrase then draws whatever numbers its file carries.
  fingering?: LiveFingering;
};

export interface IPhraseData {
  measureCount: number;
}

type PhraseType = PhraseGraphic;
interface CursorBase {}
type PlayablePhrase<T1, T2> = _PlayablePhrase;
interface Dimens2D {
  width: number;
  height: number;
}

interface SceneWithPhrases extends INoteGraphicsDatabase {
  onStartPlayingMeasure: (globalMeasure: number, phraseLocalMeasure: number) => void;
}

type SceneType = SceneWithPhrases & Phaser.Scene;

export default abstract class PhraseManager<
  PhraseDataType extends IPhraseData,
  PhraseGraphic extends PhraseType,
  Cursor extends CursorBase,
> {
  scene: SceneType;
  layoutConfig: LayoutConfig;
  maxPhrasesVisible?: number;

  // Active phrases live in a circular buffer indexed by [queueStart, queueEnd).
  phrases: (PlayablePhrase<PhraseGraphic, Cursor> | undefined)[] = [];
  protected queueStart?: number;
  protected queueEnd?: number;
  protected queueSize: number = 0;
  protected queueCapacity: number;

  // Pre-fetched phrase data buffered before being rendered.
  protected phraseLoadQueue: PhraseDataType[] = [];
  protected pendingRequests: number = 0;
  private firstRequest: boolean = true;
  /** Sequence ids this manager has issued and not yet been answered on.
   *
   *  PhraseReady is a page-wide event, so a manager left over from a remount
   *  delivers its in-flight responses to whichever manager is listening. This
   *  set is how a manager tells its own responses from a predecessor's; ids are
   *  page-unique, so membership is an exact test. Entries are dropped as they
   *  are answered, keeping this to the in-flight set. */
  protected issuedSequenceIds: Set<number> = new Set();
  private onPhraseRequested?: (params: RequestParams) => void;
  /** Phrase indices whose end has already fired a network request. Dedupes
   *  the produce side against cursor rollback / re-entry into the same phrase. */
  protected requestedForPhraseIndices: Set<number> = new Set();
  /** Phrase indices whose measure-2 has already consumed a buffered phrase
   *  into the active queue. Dedupes the consume side against rollback. */
  protected consumedForPhraseIndices: Set<number> = new Set();

  // Scene-supplied gameplay callbacks (chevrons, adaptive tempo, etc.) routed
  // from each cursor through the manager.
  protected onPlayingLastSliceCb?: (
    start: number,
    duration: number,
    durationInSecs: number,
  ) => void;
  protected onFinishPlayingPhraseCb?: (eligible?: boolean) => void;
  protected onStartPlayingMeasureCb?: (globalMeasure: number, phraseLocalMeasure: number) => void;
  protected onPhraseRemovedCb?: (uuid: string) => void;

  // Score-theme colors (applied when creating phrases/cursors).
  protected notationColor?: number;
  protected staffLineColor?: number;
  protected cursorColor?: number;

  // Current sight-reading transposition (applied when building each phrase).
  protected transposeSemitones: number = 0;
  protected modeConversion: ParallelModeConversion = 'off';

  // Live finger numbers: the tables and the current density, both undefined
  // when the manager does not finger phrases.
  protected fingeringTables?: Tables;
  protected fingeringDisplay?: FingeringDisplay;
  /** Testing aid: the clock the runtime's fingering of each phrase is timed
   *  with; undefined when fingering is not timed. */
  protected fingeringClock?: Clock;
  /** Where the last phrase built leaves each hand: what the next one starts from. */
  protected handStates: HandStates = NO_HAND_STATES;

  activePhrase?: number;
  mostRecentAddedPhrase?: PhraseGraphic;

  visiblePhraseCount: number = 0;
  totalPhrasesAdded: number = 0;
  // Number of total measures added to the scene so far
  measureCount: number = 0;
  mobile?: boolean;
  mainCursor?: Cursor;

  canvasDimens: Dimens2D;

  constructor(scene: SceneType, canvasDimens: Dimens2D, config?: PhraseManagerConfig) {
    this.layoutConfig = config?.layoutConfig ?? defaultConfig;
    this.maxPhrasesVisible = config?.maxPhrasesVisible;
    this.mobile = config?.mobile;
    this.scene = scene;
    this.canvasDimens = canvasDimens;
    this.queueCapacity = config?.maxPhrasesVisible ?? Infinity;
    this.onPhraseRequested = config?.onPhraseRequested;
    this.onPlayingLastSliceCb = config?.onPlayingLastSlice;
    this.onFinishPlayingPhraseCb = config?.onFinishPlayingPhrase;
    this.onStartPlayingMeasureCb = config?.onStartPlayingMeasure;
    this.onPhraseRemovedCb = config?.onPhraseRemoved;
    this.notationColor = config?.notationColor;
    this.staffLineColor = config?.staffLineColor;
    this.cursorColor = config?.cursorColor;
    this.transposeSemitones = config?.transposeSemitones ?? 0;
    this.modeConversion = config?.modeConversion ?? 'off';
    this.fingeringTables = config?.fingering?.tables;
    this.fingeringDisplay = config?.fingering?.display;
    this.fingeringClock = config?.fingering?.timed ? () => performance.now() : undefined;
  }

  pausePlayback() {
    this.phrases.forEach((phrase) => {
      phrase?.pause();
    });
  }

  destroy() {}

  // ---------------------------------------------------------------------------
  // Phrase data flow: producer → load queue → render
  // ---------------------------------------------------------------------------

  /** Producer delivers phrase data here. Renders immediately when a request
   *  is pending; otherwise buffers in the load queue. */
  enqueuePhraseData(data: PhraseDataType) {
    this.phraseLoadQueue.push(data);
    console.log(
      `%c[PhraseManagement] ← RESOLVED%c active=${this.queueSize} buffered=${this.phraseLoadQueue.length} pending=${this.pendingRequests}`,
      'color:#1b8a3f;font-weight:bold',
      'color:inherit',
    );
    if (this.pendingRequests > 0) {
      const next = this.phraseLoadQueue.shift()!;
      this.addPhrase(next);
      this.pendingRequests--;
    }
  }

  /** Drop every phrase this manager holds, in any stage of arriving.
   *
   *  MUS-495: a tier change swaps the content the fetches are drawing from, so
   *  anything produced for the previous tier must not reach the screen. Three
   *  populations carry it, and the flush is only as good as its weakest one:
   *  phrases already rendered into the active queue, phrases buffered in
   *  `phraseLoadQueue` awaiting a slot, and requests still in flight whose
   *  response is yet to arrive. Clearing `issuedSequenceIds` retires the last
   *  group through the guard already in `onPhraseReady` — an unissued sequence
   *  id is ignored — so a fetch that resolves after the switch is dropped
   *  instead of rendered.
   *
   *  This leaves the queue empty by design, so every caller must re-prime it
   *  (`requestInitialPhrases`) — refills are playback-driven and playback
   *  cannot start from an empty queue. */
  clearPhraseQueue() {
    this.phraseLoadQueue = [];
    this.pendingRequests = 0;
    this.issuedSequenceIds.clear();
    // The next phrase after a flush opens the new run, so it has to be one
    // playback can start from. Leaving `firstRequest` false lets the refill ask
    // without `excludeFirstNoteRest`, and a rest-first phrase strands the cursor
    // in its flashing "play this" stage with nothing to play.
    this.firstRequest = true;
    // The rendered phrases are the live symptom MUS-495 was reported against:
    // buffered content can be dropped silently, but a previous-tier phrase
    // already on screen stays on screen and gets played. Evicting them one at a
    // time through `removeOldestPhrase` is what keeps the error-feedback
    // teardown (`onPhraseRemovedCb`) and the queue bookkeeping honest; the
    // count is read once up front so the loop is bounded by the queue it
    // started on.
    const renderedPhraseCount = this.queueSize;
    for (let i = 0; i < renderedPhraseCount; i++) this.removeOldestPhrase();
    // Emptying the slots is not the same as emptying the array: eviction leaves
    // `phrases` at its old length holding `undefined`s, and `createPhrase` reads
    // `phrases.length === 0` to decide whether the phrase it is building is the
    // one that opens the score. Left as-is, the new tier's first phrase renders
    // with no time signature, no clef or brace on a horizontal layout, and no
    // shared mobile cursor — `setTranspose` drops the array for this same
    // reason when it rebuilds the queue.
    this.phrases = [];
    // Back to the state a freshly constructed manager is in, so the refill lays
    // its phrases out from the top of the queue rather than continuing the
    // ring, the clock and the counters the outgoing tier left behind.
    this.queueStart = undefined;
    this.queueEnd = undefined;
    this.activePhrase = undefined;
    this.mostRecentAddedPhrase = undefined;
    this.requestedForPhraseIndices.clear();
    this.consumedForPhraseIndices.clear();
    this.totalPhrasesAdded = 0;
    this.measureCount = 0;
  }

  /** Pull the oldest buffered phrase data; undefined if the load queue is empty. */
  protected getNextAvailablePhraseData(): PhraseDataType | undefined {
    return this.phraseLoadQueue.shift();
  }

  /** Notify the producer that another phrase is needed. */
  requestPhrase() {
    console.log(
      `%c[PhraseManagement] → REQUEST%c active=${this.queueSize} buffered=${this.phraseLoadQueue.length} pending=${this.pendingRequests} firstRequest=${this.firstRequest}`,
      'color:#b8860b;font-weight:bold',
      'color:inherit',
    );
    const sequenceId = allocatePhraseSequenceId();
    const handStates = this.handStatesForNextRequest();
    this.issuedSequenceIds.add(sequenceId);
    this.onPhraseRequested?.({
      excludeFirstNoteRest: this.firstRequest,
      sequenceId,
      ...(handStates === undefined ? {} : { handStates }),
      ...this.transpositionOfRequest(),
    });
    this.firstRequest = false;
  }

  /** Where the phrase about to be requested will start from, when that is known. */
  protected handStatesForNextRequest(): HandStates | undefined {
    return undefined;
  }

  /** The transposition a request carries: none unless the manager fingers phrases. */
  protected transpositionOfRequest(): { transposition?: number } {
    return {};
  }

  /** Ask again for a phrase whose answer was discarded, saying where the hands
   *  are. The request keeps its sequence id, and with it its place in the order. */
  protected requestPhraseAgain(sequenceId: number, handStates: HandStates) {
    this.issuedSequenceIds.add(sequenceId);
    this.onPhraseRequested?.({
      excludeFirstNoteRest: false,
      sequenceId,
      handStates,
      ...this.transpositionOfRequest(),
    });
  }

  /** Render the next buffered phrase if available; otherwise request one. */
  protected consumeOrRequestNextPhrase() {
    const next = this.getNextAvailablePhraseData();
    if (next !== undefined) {
      this.addPhrase(next);
    } else {
      this.requestPhrase();
      this.pendingRequests++;
    }
  }

  /** Pull the next buffered phrase into the active queue. If the buffer is
   *  empty (network slower than playback), mark a deferred consume so the
   *  arrival auto-renders via `enqueuePhraseData`. Used by the measure-2
   *  trigger; the matching network request fires at phrase-end. */
  protected consumeBufferedPhrase() {
    const next = this.getNextAvailablePhraseData();
    if (next !== undefined) {
      this.addPhrase(next);
    } else {
      this.pendingRequests++;
    }
  }

  // ---------------------------------------------------------------------------
  // Playback lifecycle hooks
  // ---------------------------------------------------------------------------

  onFinishPlayingPhrase() {
    this.consumeOrRequestNextPhrase();
  }

  // ---------------------------------------------------------------------------
  // Active phrase queue (circular buffer)
  // ---------------------------------------------------------------------------

  addPhrase(phraseData: PhraseDataType) {
    const phrase = this.createPhrase(phraseData);
    this.mostRecentAddedPhrase = phrase.graphic as PhraseGraphic;
    this.totalPhrasesAdded++;
    this.measureCount += phraseData.measureCount;
    this.addPhraseToActiveQueue(phrase);
  }

  abstract createPhrase(phraseData: PhraseDataType): PlayablePhrase<PhraseGraphic, Cursor>;

  protected addPhraseToActiveQueue(phrase: PlayablePhrase<PhraseGraphic, Cursor>) {
    if (this.queueSize >= this.queueCapacity) {
      this.removeOldestPhrase();
    }
    if (this.queueStart === undefined || this.queueEnd === undefined) {
      this.queueStart = 0;
      this.queueEnd = 0;
    }
    this.phrases[this.queueEnd] = phrase;
    this.queueEnd = (this.queueEnd + 1) % this.queueCapacity;
    this.queueSize++;
    this.visiblePhraseCount++;
  }

  protected removeOldestPhrase() {
    if (this.queueStart === undefined) return;
    const oldest = this.phrases[this.queueStart];
    console.debug(
      `[phrase-destroy] PhraseManager.removeOldestPhrase slot=${this.queueStart} uuid=${oldest?.graphic._data.uuid} currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded}`,
    );
    if (oldest) this.onPhraseRemovedCb?.(oldest.graphic._data.uuid);
    oldest?.destroy();
    this.phrases[this.queueStart] = undefined;
    this.queueStart = (this.queueStart + 1) % this.queueCapacity;
    this.queueSize--;
    this.visiblePhraseCount--;
  }

  // ---------------------------------------------------------------------------
  // Queue iteration / lookup
  // ---------------------------------------------------------------------------

  getFirstPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.queueStart === undefined) return undefined;
    return this.phrases[this.queueStart];
  }

  getLastPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.queueEnd === undefined) return undefined;
    const idx = (this.queueEnd - 1 + this.queueCapacity) % this.queueCapacity;
    return this.phrases[idx];
  }

  getActivePhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.activePhrase === undefined) return undefined;
    return this.phrases[this.activePhrase];
  }

  /** Index of the slot following the active phrase, wrapping by queue capacity.
   *  Replaces the scene's old hard-coded `(activePhrase + 1) % 4`. */
  getNextIndex(): number {
    const cap = Number.isFinite(this.queueCapacity) ? this.queueCapacity : this.phrases.length || 1;
    return ((this.activePhrase ?? 0) + 1) % cap;
  }

  getNextPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    return this.phrases[this.getNextIndex()];
  }

  findPhraseContainingMeasure(
    measure: number,
  ): [number, PlayablePhrase<PhraseGraphic, Cursor>] | undefined {
    for (const i of getCircularQueueIndices(this.queueStart, this.queueSize, this.queueCapacity)) {
      const phrase = this.phrases[i];
      if (phrase?.graphic.containsMeasure(measure)) return [i, phrase];
    }
    return undefined;
  }

  // ---------------------------------------------------------------------------
  // Active phrase navigation
  // ---------------------------------------------------------------------------

  activateNextPhrase() {
    if (this.activePhrase === this.queueEnd) {
      Logger.warn('[PhraseManager] No next phrase to activate');
      return;
    }
    const next = this.activePhrase === undefined ? 0 : (this.activePhrase + 1) % this.queueCapacity;
    if (!this.phrases[next]) {
      Logger.warn('[PhraseManager] No next phrase to activate');
      return;
    }
    this.activatePhrase(next);
  }

  activatePreviousPhrase() {
    if (this.activePhrase === this.queueStart) {
      Logger.warn('[PhraseManager] No previous phrase to activate');
      return;
    }
    const prev =
      this.activePhrase === undefined
        ? 0
        : (this.activePhrase - 1 + this.queueCapacity) % this.queueCapacity;
    if (!this.phrases[prev]) {
      Logger.warn('[PhraseManager] No previous phrase to activate');
      return;
    }
    this.activatePhrase(prev);
  }

  activatePhrase(idx: number) {
    if (this.activePhrase !== undefined) this.phrases[this.activePhrase]?.deactivate();
    this.phrases[idx]?.activate(true);
    this.activePhrase = idx;
  }

  activatePhraseByMeasure(measure: number) {
    const result = this.findPhraseContainingMeasure(measure);
    if (result !== undefined) {
      this.activatePhrase(result[0]);
    }
  }

  // ---------------------------------------------------------------------------
  // Frame update / resize
  // ---------------------------------------------------------------------------

  update(_delta: number) {
    this.phrases.forEach((phrase) => {
      phrase?.update();
    });
  }

  getViewportSize() {
    const cam = this.scene.cameras.main;
    return { width: cam.width, height: cam.height };
  }

  resize(dimens: Dimens2D) {
    if (dimens === this.canvasDimens) return;
    this.canvasDimens = dimens;
    this.onResize(dimens);
  }

  abstract onResize(dimens: Dimens2D): void;
}

export class PhraseManagerMuseflowRenderer extends PhraseManager<
  PhraseData,
  PhraseGraphic,
  CursorMobile
> {
  playbackStarted: boolean = false;
  lastLayout?: ResizeParams;
  eventStream!: EventStreamPhaser2;
  /** Lesson zoom ceiling, recomputed from the layout during onResize. The scene
   *  reads this when running its zoom tween. */
  maxZoomLevel: number = 1;

  /** The lesson zoom as a fraction of {@link maxZoomLevel}; the scene keeps it
   *  in sync. Fully zoomed in until told: the view that shows least. */
  private relativeZoom: number = 1;

  /** When true, cursors are created render-hidden so the cursor-visibility
   *  toggle (off) is respected from the first frame — including phrases created
   *  later during gameplay. The scene keeps this in sync via setCursorVisible. */
  cursorHidden: boolean = false;

  /** Running clock used to assign each phrase a startTimestamp at queue time.
   *  Advanced by the full measure-duration of every phrase as it enters the
   *  active queue, guaranteeing phrases are timestamped strictly in order. */
  private accumulatingTimestamp: number = 0;

  /** Count of initial phrases still awaited in request order. While > 0, the
   *  manager is in its "init" phase and reorders out-of-order responses. */
  private initialPhrasesRemaining: number = 0;
  /** The request sequence id the init phase is waiting to render next. */
  private nextInitSequenceId: number = 0;
  /** Initial responses that arrived ahead of their turn, kept sorted ascending
   *  by sequenceId until the gap before them is filled. */
  private initBacklog: { sequenceId: number; phrase: Phrase }[] = [];
  /** How often each opening request was sent again for its join, by sequence id. */
  private openingJoinRetries: Map<number, number> = new Map();

  constructor(scene: SceneType, canvasDimens: Dimens2D, config?: PhraseManagerConfig) {
    super(scene, canvasDimens, config);
    if (config?.eventStream) this.eventStream = config.eventStream;
    ReactToPhaserListeners.PhraseReady(this.onPhraseReady, this);
  }

  destroy() {
    console.debug(
      `[phrase-destroy] PhraseManagerMuseflowRenderer.destroy currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded}`,
    );
    RemoveListener('PhraseReady');
    this.phrases.forEach((phrase) => phrase?.destroy());
    this.mainCursor?.destroy();
    this.mainCursor = undefined;
  }

  /** MUS-495: the base flush retires the buffered, rendered and in-flight
   *  phrases; the renderer's own per-run state has to go with them.
   *
   *  `initialPhrasesRemaining` is the load-bearing one: left set, later
   *  arrivals park in `initBacklog` waiting on sequence ids that were just
   *  invalidated and will never be delivered, so the queue never refills after
   *  a tier change. The rest is the same teardown `setTranspose` does when it
   *  rebuilds the queue — the phrase clock has to restart at zero alongside the
   *  timekeeper React resets, or the new tier's phrases are timestamped past
   *  the end of the outgoing tier and never come up for playback. */
  override clearPhraseQueue() {
    super.clearPhraseQueue();
    this.initialPhrasesRemaining = 0;
    this.initBacklog = [];
    this.mainCursor?.destroy();
    this.mainCursor = undefined;
    this.eventStream?.reset();
    this.accumulatingTimestamp = 0;
  }

  /** The play session this manager has already filled its queue for.
   *
   *  MUS-495: two things can prime the queue for the same session — the scene
   *  booting with a session already in hand, and the session-committed event
   *  from React — and which arrives first depends on a boot race. Recording the
   *  session makes priming idempotent: whichever gets there first fills the
   *  queue, and the other is a no-op instead of a second round trip that throws
   *  the first one's phrases away. */
  primedForSession?: string | null;

  /** Rebuild the queue for a session that has just taken over. No-op if this
   *  manager has already filled its queue for that session.
   *
   *  Returns whether it actually rebuilt, because a rebuild destroys the shared
   *  mobile cursor and the first-phrase brace — the two objects the camera is
   *  wired to — and the scene has to re-wire against their replacements. */
  restartForSession(playSessionId: string): boolean {
    if (this.primedForSession === playSessionId) return false;
    this.primedForSession = playSessionId;
    this.clearPhraseQueue();
    this.requestInitialPhrases();
    return true;
  }

  /** Kick off the initial request-driven fill so the visible queue is populated
   *  before playback. The producer (React) answers each request via PhraseReady. */
  requestInitialPhrases() {
    const count = this.mobile ? 3 : 2;
    // Enter the init phase: these `count` requests fire back-to-back here and
    // (on a fresh manager) take sequence ids 0..count-1. Their responses may
    // resolve out of order, so onPhraseReady reorders them by sequenceId before
    // rendering, guaranteeing the initial on-screen phrases match request order.
    this.initialPhrasesRemaining = count;
    // The loop below allocates its ids synchronously and back-to-back, so the
    // run starts at whatever the page-wide allocator is about to hand out.
    this.nextInitSequenceId = peekNextPhraseSequenceId();
    this.initBacklog = [];
    this.openingJoinRetries = new Map();
    for (let i = 0; i < count; i++) {
      this.consumeOrRequestNextPhrase();
    }
  }

  /** Producer (React) hands us a freshly fetched, untransformed phrase. We
   *  build the PhraseData here (applying the current transposition) and
   *  buffer/render it through the queue.
   *
   *  During the init phase the initial phrases must enter the queue in the
   *  order they were requested. Responses can arrive out of order, so any
   *  phrase that isn't the next-expected sequence id is parked in a sorted
   *  backlog; once the gap is filled we drain the backlog in order. */
  private onPhraseReady = ({ phrase, sequenceId }: { phrase: Phrase; sequenceId: number }) => {
    // PhraseReady is page-wide and `destroy()` drops every listener for it, so
    // after a remount this manager is the one holding the bus while the
    // outgoing manager's requests are still resolving. Those responses belong
    // to a queue that no longer exists; rendering them would put phrases we
    // never asked for on screen, and parking them in `initBacklog` would wedge
    // the init gate behind ids that will never be requested. Drop them.
    if (!this.issuedSequenceIds.delete(sequenceId)) {
      console.debug(
        `[phrase-seq] IGNORED seq=${sequenceId} — not issued by this manager (stale remount response)`,
      );
      return;
    }

    if (this.initialPhrasesRemaining > 0) {
      // Insert keeping initBacklog sorted ascending by sequenceId.
      const insertAt = this.initBacklog.findIndex((e) => e.sequenceId > sequenceId);
      const entry = { sequenceId, phrase };
      if (insertAt === -1) this.initBacklog.push(entry);
      else this.initBacklog.splice(insertAt, 0, entry);

      // Drain every contiguous run starting at the next-expected sequence id.
      while (
        this.initialPhrasesRemaining > 0 &&
        this.initBacklog[0]?.sequenceId === this.nextInitSequenceId
      ) {
        const next = this.initBacklog.shift()!;
        if (this.openingJoinAsksAgain(next.phrase, next.sequenceId)) {
          // The answer is discarded and its turn stays open: the backlog now
          // starts past it, which ends the drain until the new answer is in.
          this.requestOpeningPhraseAgain(next.sequenceId);
          continue;
        }
        this.nextInitSequenceId++;
        this.initialPhrasesRemaining--;
        this.enqueuePhraseData(this.buildPhraseData(next.phrase, next.sequenceId));
      }
      return;
    }

    this.enqueuePhraseData(this.buildPhraseData(phrase, sequenceId));
  };

  /** Whether an opening phrase is discarded and asked for again. The opening
   *  phrases are requested together, before the hands are anywhere, so nothing
   *  chose this one to follow the phrase before it. Once that phrase is built
   *  the manager makes the check selection would have made: the phrase goes
   *  back when the hands cannot get into it, or only by a hop. It is kept when
   *  selection has said the tier holds nothing better, and once it has been
   *  asked for {@link OPENING_JOIN_RETRY_LIMIT} times again. */
  private openingJoinAsksAgain(phrase: Phrase, sequenceId: number): boolean {
    if (this.fingeringTables === undefined || phrase.joinFallback) return false;
    if ((this.openingJoinRetries.get(sequenceId) ?? 0) >= OPENING_JOIN_RETRY_LIMIT) return false;
    const join = this.joinOf(this.fingeringTables, phrase);
    return !join.compatible || join.hops > 0;
  }

  private requestOpeningPhraseAgain(sequenceId: number) {
    this.openingJoinRetries.set(sequenceId, (this.openingJoinRetries.get(sequenceId) ?? 0) + 1);
    this.requestPhraseAgain(sequenceId, this.handStates);
  }

  /** What a phrase's written notes sound as under the transposition and the
   *  mode conversion in force. */
  private soundingKeyOf(phrase: Phrase): Key {
    return soundingKeyOf(phrase.musicXML.xml, this.transposeSemitones, this.modeConversion);
  }

  /** Whether the hands can get into a phrase, as it now sounds, from where they
   *  are. A check that cannot be made never costs a phrase: it counts as passed. */
  private joinOf(tables: Tables, phrase: Phrase): Join {
    try {
      const { levelTier } = phrase;
      const tier = levelTier ? tierOf(tables, levelTier.level, levelTier.tier) : null;
      const written = phrase.getMeasureData().serializedIterator;
      return joinOfPhraseNotes(
        tables,
        tier,
        handLinesOf(written),
        this.handStates,
        this.soundingKeyOf(phrase),
      );
    } catch (error) {
      reportError(error, 'fingering.join');
      return { compatible: true, hops: 0 };
    }
  }

  /** Build a PhraseData from a raw Phrase, applying the manager's current
   *  transposition / mode-conversion to the iterator. When the manager fingers
   *  phrases, this is also where the phrase gets its finger numbers, from where
   *  the phrase built before it left each hand: phrases must be built in the
   *  order they are played. */
  private buildPhraseData(phrase: Phrase, sequenceId?: number): PhraseData {
    const raw = phrase.getMeasureData();
    const keySignature = getTransposedMusicXmlKeySignature(
      phrase.musicXML.xml,
      this.transposeSemitones,
      this.modeConversion,
    );
    const enteringHandStates = this.handStates;
    const cursorData: CursorData = {
      serializedIterator: this.withLiveFingering(
        phrase,
        raw.serializedIterator,
        transformSerializedIteratorPitches(
          raw.serializedIterator,
          this.transposeSemitones,
          this.modeConversion,
          phrase.musicXML.xml,
        ),
      ),
      measureRects: raw.measureRects,
    };
    const phraseData = new PhraseData(
      phrase,
      this.totalPhrasesAdded,
      cursorData,
      phrase.uuid,
      phrase.musicXML.timeSignatures[0],
      true, // showTimeSig — overridden per layout in createPhrase
      true, // showBrace
      false, // endMeasure
      true, // showClef
      0, // startTimestamp — assigned at queue time in addPhrase
      keySignature?.fifths,
      keySignature?.label,
    );
    phraseData.sequenceId = sequenceId;
    phraseData.fingeringMinimumNeed = this.fingeringMinimumNeed();
    phraseData.enteringHandStates = enteringHandStates;
    phraseData.leavingHandStates = this.handStates;
    return phraseData;
  }

  /** The phrase's notes as they will be drawn. When the manager fingers
   *  phrases, the computed numbers replace the ones carried by the phrase file
   *  and the hands move on to where this phrase leaves them. */
  private withLiveFingering(
    phrase: Phrase,
    written: PrerenderedPhraseData[],
    sounding: PrerenderedPhraseData[],
  ): PrerenderedPhraseData[] {
    if (this.fingeringTables === undefined) return sounding;
    const fingering = this.fingeringOf(this.fingeringTables, phrase, written);
    this.handStates = fingering.states;
    return replaceFingering(sounding, fingering.fingeringByNoteId);
  }

  /** A phrase's finger numbers in the key it now sounds in, from where the
   *  hands are. Finger numbers must never cost a phrase: if they cannot be
   *  computed the phrase is drawn without any and the next one places the hands
   *  afresh. */
  private fingeringOf(
    tables: Tables,
    phrase: Phrase,
    written: PrerenderedPhraseData[],
  ): PhraseFingering {
    try {
      const { levelTier } = phrase;
      const tier = levelTier ? tierOf(tables, levelTier.level, levelTier.tier) : null;
      const fingering = fingerPhraseNotes(
        tables,
        tier,
        handLinesOf(written),
        this.handStates,
        this.soundingKeyOf(phrase),
        this.fingeringClock,
      );
      if (fingering.runtimeMilliseconds !== undefined) {
        NotifyReactEvents.SightReadingPhraseFingered({
          milliseconds: fingering.runtimeMilliseconds,
        });
      }
      return fingering;
    } catch (error) {
      reportError(error, 'fingering.phrase');
      return { fingeringByNoteId: new Map(), states: NO_HAND_STATES };
    }
  }

  /** Testing aid: how the phrase the cursor is on was fingered and drawn, with
   *  the other phrases on screen. Before the cursor is on any, the first phrase
   *  on screen stands in. Null when the manager does not finger phrases or
   *  holds none. */
  fingeringReport(): SightReadingFingeringReport | null {
    const tables = this.fingeringTables;
    const display = this.fingeringDisplay;
    const underCursor = (this.getActivePhrase() ?? this.getFirstPhrase())?.graphic._data;
    if (tables === undefined || display === undefined || underCursor === undefined) return null;
    const minimumNeed = needThreshold(tables, display);
    const onScreen = getCircularQueueIndices(this.queueStart, this.queueSize, this.queueCapacity)
      .map((index) => this.phrases[index]?.graphic._data)
      .filter((phraseData): phraseData is PhraseData => phraseData !== undefined);
    const cursorPlace = onScreen.indexOf(underCursor);
    return {
      tablesVersion: tables.tables_version,
      contractVersion: tables.contract_version,
      density: display,
      minimumNeed: Number.isFinite(minimumNeed) ? minimumNeed : null,
      transposition: this.transposeSemitones,
      modeConversion: this.modeConversion,
      phrase: this.fingeringReportOf(tables, underCursor, minimumNeed),
      otherPhrasesOnScreen: onScreen.flatMap((phraseData, place) =>
        phraseData === underCursor
          ? []
          : [
              {
                position: place - cursorPlace,
                ...this.fingeringReportOf(tables, phraseData, minimumNeed),
              },
            ],
      ),
    };
  }

  private fingeringReportOf(
    tables: Tables,
    phraseData: PhraseData,
    minimumNeed: number,
  ): PhraseFingeringReport {
    const { phrase } = phraseData;
    const { levelTier } = phrase;
    const tierFolder = levelTier ? tierOf(tables, levelTier.level, levelTier.tier) : null;
    return phraseFingeringReportOf(
      {
        phraseId: phrase.uuid,
        levelTier,
        tierFolder,
        joinFallback: phrase.joinFallback,
        key: this.soundingKeyOf(phrase),
        keyLabel: phraseData.keySignatureLabel ?? null,
        written: phrase.getMeasureData().serializedIterator,
        drawn: phraseData.serializedIterator,
        entering: phraseData.enteringHandStates ?? NO_HAND_STATES,
        leaving: phraseData.leavingHandStates ?? NO_HAND_STATES,
        numbersShown: {
          rh: tierFolder !== null && showsNumbers(tables, tierFolder, 'rh'),
          lh: tierFolder !== null && showsNumbers(tables, tierFolder, 'lh'),
        },
      },
      minimumNeed,
    );
  }

  /** The lowest need score whose number is drawn at the current density. */
  private fingeringMinimumNeed(): number | undefined {
    return this.fingeringTables === undefined || this.fingeringDisplay === undefined
      ? undefined
      : needThreshold(this.fingeringTables, this.fingeringDisplay);
  }

  /** Live density change: the on-screen phrases redraw their numbers in place,
   *  and phrases fetched but not drawn yet will come out at the new density. */
  setFingeringDisplay(display: FingeringDisplay) {
    if (this.fingeringTables === undefined || display === this.fingeringDisplay) return;
    this.fingeringDisplay = display;
    const minimumNeed = needThreshold(this.fingeringTables, display);
    this.phrases.forEach((phrase) => phrase?.graphic.setFingeringMinimumNeed(minimumNeed));
    this.phraseLoadQueue.forEach((buffered) => {
      buffered.fingeringMinimumNeed = minimumNeed;
    });
  }

  /** The hand states go out with a request only when the phrase it asks for
   *  will follow the last one built. While an earlier request is unanswered the
   *  requested phrase follows that one instead (a phrase waiting its turn in the
   *  opening fill is always waiting for such a request). And only once a hand
   *  has been placed: before that there is nothing to say. */
  protected override handStatesForNextRequest(): HandStates | undefined {
    const predecessorBuilt = this.issuedSequenceIds.size === 0;
    const anyHandPlaced = this.handStates.rh !== null || this.handStates.lh !== null;
    return this.fingeringTables !== undefined && predecessorBuilt && anyHandPlaced
      ? this.handStates
      : undefined;
  }

  /** The scene y range the camera shows once its zoom has settled. */
  private rowsInView(): { top: number; bottom: number } {
    const { height } = this.getViewportSize();
    const cropped = (height / 2) * (1 - 1 / (this.relativeZoom * this.maxZoomLevel));
    return { top: cropped, bottom: height - cropped };
  }

  /** Tell every staff line on screen the room it has for finger numbers. The
   *  staves are never sized for them: a row is drawn in the space the layout
   *  already leaves, which ends at the edge of the view or, between two staff
   *  lines stacked on the page, half-way to the other. */
  private keepFingerNumbersInView() {
    if (this.fingeringTables === undefined) return;
    const view = this.rowsInView();
    const stacked = this.layoutConfig.layoutDirection === PhraseLayout.Vertical;
    const staffLines = this.phrases
      .flatMap((phrase) => (phrase ? [phrase.graphic] : []))
      .sort((a, b) => a.y - b.y);
    const halfWay = (upper: PhraseGraphic | undefined, lower: PhraseGraphic | undefined) => {
      const from = upper?.getBottomLineSceneY();
      const to = lower?.getTopLineSceneY();
      return stacked && from !== undefined && to !== undefined ? (from + to) / 2 : undefined;
    };
    staffLines.forEach((staffLine, place) => {
      staffLine.keepFingerNumbersWithin(
        halfWay(staffLines[place - 1], staffLine) ?? view.top,
        halfWay(staffLine, staffLines[place + 1]) ?? view.bottom,
      );
    });
  }

  /** Live zoom change: what the camera shows changes, and with it the room the
   *  outer rows of finger numbers have. */
  setRelativeZoom(relativeZoom: number) {
    this.relativeZoom = relativeZoom;
    this.keepFingerNumbersInView();
  }

  /** Every request of a manager that fingers phrases says what key they will
   *  sound in. */
  protected override transpositionOfRequest(): { transposition?: number } {
    return this.fingeringTables === undefined ? {} : { transposition: this.transposeSemitones };
  }

  /** Phrases fetched but not drawn yet were built for the transposition that
   *  was in force when they arrived; build them again for the current one. */
  private rebuildBufferedPhrases() {
    this.phraseLoadQueue = this.phraseLoadQueue.map((buffered) =>
      this.buildPhraseData(buffered.phrase, buffered.sequenceId),
    );
  }

  /** Live transpose: re-transpose the on-screen phrases in place. Rebuilds the
   *  active queue from the same Phrase objects (so timestamps/layout are
   *  identical) and re-populates the gameplay event stream. */
  setTranspose(semitones: number, modeConversion: ParallelModeConversion): number {
    if (semitones === this.transposeSemitones && modeConversion === this.modeConversion) {
      return this.queueSize;
    }
    this.transposeSemitones = semitones;
    this.modeConversion = modeConversion;

    // Capture the currently-queued phrases in play order before tearing down.
    const activePhrases: Phrase[] = [];
    for (const i of getCircularQueueIndices(this.queueStart, this.queueSize, this.queueCapacity)) {
      const phrase = this.phrases[i]?.graphic._data.phrase;
      if (phrase) activePhrases.push(phrase);
    }
    // The hands go back to where they were before the first phrase still
    // queued, and every phrase from there on is fingered again, in order.
    const firstQueued = this.getFirstPhrase()?.graphic._data ?? this.phraseLoadQueue[0];
    this.handStates = firstQueued?.enteringHandStates ?? this.handStates;
    if (activePhrases.length === 0) {
      this.rebuildBufferedPhrases();
      return 0;
    }

    // Tear down current rendering + the (now stale) gameplay events, then reset
    // all queue/clock bookkeeping so the rebuild reproduces identical positions.
    console.debug(
      `[phrase-destroy] PhraseManagerMuseflowRenderer.setTranspose teardown currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded} semitones=${semitones}`,
    );
    this.phrases.forEach((p) => p?.destroy());
    this.mainCursor?.destroy();
    this.mainCursor = undefined;
    this.eventStream?.reset();
    this.phrases = [];
    this.queueStart = undefined;
    this.queueEnd = undefined;
    this.queueSize = 0;
    this.visiblePhraseCount = 0;
    this.totalPhrasesAdded = 0;
    this.measureCount = 0;
    this.accumulatingTimestamp = 0;
    this.activePhrase = 0;
    this.mostRecentAddedPhrase = undefined;
    this.requestedForPhraseIndices.clear();
    this.consumedForPhraseIndices.clear();

    activePhrases.forEach((phrase) => this.addPhrase(this.buildPhraseData(phrase)));
    this.rebuildBufferedPhrases();

    // Reposition (desktop layout is driven by onResize) and snap the cursor home.
    this.onResize(this.canvasDimens);
    this.getFirstPhrase()?.activate(false, true);
    this.notifyActiveKey();
    return activePhrases.length;
  }

  notifyActiveKey() {
    NotifyReactEvents.SightReadingKeyChanged({
      label: this.getActivePhrase()?.graphic._data.keySignatureLabel ?? null,
    });
  }

  override activatePhrase(idx: number) {
    super.activatePhrase(idx);
    this.notifyActiveKey();
  }

  /** Assign the phrase its timestamp from the running clock, register its time
   *  signature with the timekeeper, then advance the clock by the phrase's full
   *  duration so the next phrase starts exactly where this one ends. */
  addPhrase(phraseData: PhraseData) {
    console.debug(
      `[phrase-seq] RENDER seq=${phraseData.sequenceId} uuid=${phraseData.uuid} order=${this.totalPhrasesAdded}`,
    );
    const timeSignature = phraseData.timeSignature;
    phraseData.startTimestamp = this.accumulatingTimestamp;
    // PhraseData copies startTimestamp into trebleClef/bassClef at construction
    // time. We construct PhraseData with a placeholder (0) and assign the real
    // value here, so the clef copies need to be synced too — otherwise
    // Clef.getNotePosition's `note.timestamp + clef.startTimestamp === target`
    // check uses 0 for every phrase past the first, and lookups silently miss.
    phraseData.trebleClef.startTimestamp = this.accumulatingTimestamp;
    phraseData.bassClef.startTimestamp = this.accumulatingTimestamp;
    phraseData.phrase.setStartTimestamp(this.accumulatingTimestamp);
    console.debug(
      `[phrase-add] uuid=${phraseData.uuid} startTs=${phraseData.startTimestamp} measures=${phraseData.measureCount} timeSig=${timeSignature.numerator}/${timeSignature.denominator} totalAdded=${this.totalPhrasesAdded} currentCount=${this.queueSize}`,
    );
    // measureCount here is the cumulative measure index before this phrase.
    this.eventStream?.timeKeeper.setTimeSignatures(this.measureCount, [timeSignature]);
    super.addPhrase(phraseData);
    if (this.queueSize === 1) {
      this.notifyActiveKey();
    }
    const measureDuration = timeSignature.numerator / timeSignature.denominator;
    this.accumulatingTimestamp += phraseData.measureCount * measureDuration;
    this.eventStream?.addEvents2(
      new Iterator(phraseData.serializedIterator, phraseData.phrase),
      phraseData.startTimestamp,
    );
    // Desktop: re-layout all visible phrases now that a new one is in the queue
    // (createPhrase only positions horizontal/mobile layouts in positionPhrase).
    if (!this.mobile) this.onResize(this.canvasDimens);
  }

  createPhrase(phraseData: PhraseData): PlayablePhrase<PhraseGraphic, CursorMobile> {
    const isFirstPhrase = this.phrases.length === 0;
    const horizontal = this.layoutConfig.layoutDirection === PhraseLayout.Horizontal;
    const drawSymbols = !horizontal || isFirstPhrase;

    phraseData.showClef = drawSymbols;
    phraseData.showBrace = drawSymbols;
    // The time signature belongs only on the first phrase (matching the OSMD and
    // EventBus2 scenes). Repeating it on every stacked line reserved time-sig
    // leading space on lines that don't render one, pushing their first notes in
    // as far as the first line's. Clef/brace still repeat per line as expected.
    phraseData.showTimeSig = isFirstPhrase;

    const dpr = window.devicePixelRatio;
    const phraseGraphic = new PhraseGraphic(
      this.scene,
      0,
      {
        globalScaling: 1,
        pitchLineGap: 20,
        pitchLineWidth: 1 * dpr,
        measureLineWidth: 2 * dpr,
        firstNoteGap: horizontal ? 40 : undefined,
        clefGap: 100,
        clefSymbolMargin: horizontal ? 10 : undefined,
        doubleEndLines: false,
        notationColor: this.notationColor,
        staffLineColor: this.staffLineColor,
      },
      phraseData,
      'Fade',
      this.measureCount,
      phraseData.startTimestamp,
      this.totalPhrasesAdded % 2 === 1,
    );

    let cursor: CursorMobile | undefined;
    // Cursor to pass to positionPhrase for sizing/placement. Differs from the
    // cursor stored on the PlayablePhrase on mobile, where mainCursor is shared
    // across phrases (and would be double-destroyed if a phrase referenced it).
    let cursorToPosition: CursorMobile | undefined;
    if (horizontal) {
      if (this.mainCursor === undefined) {
        // First mobile phrase: create the shared cursor and size it here.
        this.mainCursor = this.createCursor(phraseData, isFirstPhrase);
        cursorToPosition = this.mainCursor;
      } else {
        // Subsequent mobile phrases: extend the shared cursor's iterator so it
        // knows how to play past the end of the previous phrase. Without this
        // the iterator stops once it crosses into a phrase it has no
        // phraseInfo entry for.
        this.mainCursor.addPhrase(
          phraseData.serializedIterator,
          phraseData.uuid,
          phraseData.timeSignature,
          this.totalPhrasesAdded,
        );
        console.debug('[phrase-add] extending shared cursor', this.mainCursor?.iterator);
      }
    } else {
      cursor = this.createCursor(phraseData, isFirstPhrase);
      cursorToPosition = cursor;
    }

    this.positionPhrase(phraseGraphic, cursorToPosition);

    return new _PlayablePhrase(phraseGraphic, cursor);
  }

  private createCursor(phraseData: PhraseData, isFirstPhrase: boolean): CursorMobile {
    const sceneAsDb = this.scene;
    // Captured at creation so the manager's request-gating can dedupe per phrase.
    const phraseIndex = this.totalPhrasesAdded;
    // Each cursor callback drives BOTH the manager's own request/bookkeeping
    // logic and the scene-supplied gameplay callback. Arrow functions keep the
    // manager as `this` even though CursorMobile invokes them via .call/.bind.
    const onLastSlice = (start: number, duration: number, durationInSecs: number) => {
      this.onPlayingLastSliceCb?.(start, duration, durationInSecs);
    };
    const onFinishPlaying = (eligible?: boolean) => {
      // Phrase complete fires the network REQUEST for the next phrase. The
      // arrival is buffered, not rendered; the matching consume at
      // measure-2 of the next phrase performs the queue insertion. Firing
      // the request this early (vs. the prior measure-2-of-current trigger)
      // gives the network a full extra phrase of lead time, important at
      // high tempo (250+ bpm) where a slow response would otherwise miss
      // the consume window. Dedupe per phrase index so cursor rollback /
      // re-entry into the same phrase doesn't double-request.
      const cursorPhraseIdx = this.mainCursor?.getEffectivePhraseIndex() ?? phraseIndex;
      if (!this.requestedForPhraseIndices.has(cursorPhraseIdx)) {
        this.requestedForPhraseIndices.add(cursorPhraseIdx);
        this.requestPhrase();
      }
      this.onFinishPlayingPhraseCb?.(eligible);
    };
    const onStartPlayingMeasure = (globalMeasure: number, phraseLocalMeasure: number) => {
      // Measure 2 (phrase-local) CONSUMES the phrase that was requested at
      // the prior phrase's end — pulls it from the buffer and inserts into
      // the active queue. If the buffer is empty (network slow), the
      // consume is deferred via `pendingRequests` and the next arrival
      // auto-renders.
      //
      // Desktop skips phrase 0: phrase 0's end is the very first request
      // trigger, so there's nothing in the buffer to consume yet, and the
      // queue already holds phrase 0 + phrase 1 (cap=2) from initial preload.
      // Dedupe per phrase index against cursor rollback.
      if (phraseLocalMeasure === 2) {
        const cursorPhraseIdx = this.mainCursor?.getEffectivePhraseIndex() ?? phraseIndex;
        const skipForDesktopPhraseZero = !this.mobile && cursorPhraseIdx === 0;
        if (!skipForDesktopPhraseZero && !this.consumedForPhraseIndices.has(cursorPhraseIdx)) {
          this.consumedForPhraseIndices.add(cursorPhraseIdx);
          this.consumeBufferedPhrase();
        }
      }
      this.onStartPlayingMeasureCb?.(globalMeasure, phraseLocalMeasure);
    };
    // Start with an empty iterator; cursor.addPhrase below populates phraseInfo
    // at the correct index so phraseInfo[iterator.phraseIndex] resolves. (If we
    // pass serializedIterator to the constructor it pushes phraseInfo[0] regardless
    // of which phrase this is, which breaks every phrase past the first.)
    const lessonCursor = new CursorMobile(
      sceneAsDb,
      0,
      0,
      0,
      0,
      this.eventStream,
      new IteratorExtensible(
        sceneAsDb,
        [],
        phraseData.phrase,
        phraseData.timeSignature,
        undefined,
        undefined,
        phraseIndex,
      ),
      phraseData.serializedIterator,
      onLastSlice,
      onFinishPlaying,
      undefined,
      // Non-first phrases start invisible — `phrase.activate()` calls
      // `cursor.enable()` which restores the visible alpha once it's that
      // phrase's turn to play. Without this the pre-rendered upcoming
      // phrase's cursor sits visibly at (0, phraseY) at scene load.
      !isFirstPhrase,
      phraseData.startTimestamp,
      phraseIndex,
      isFirstPhrase,
      onStartPlayingMeasure,
      undefined,
      this.cursorColor,
    );
    // Mirror the old scene's reset + addPhrase: gives the iterator its
    // startingMeasure / timestampOffset, then populates phraseInfo[phraseIndex].
    lessonCursor.reset(
      new IteratorExtensible(
        sceneAsDb,
        [],
        phraseData.phrase,
        phraseData.timeSignature,
        undefined,
        undefined,
        phraseIndex,
      ),
      phraseData.startTimestamp,
      this.measureCount,
    );
    lessonCursor.addPhrase(
      phraseData.serializedIterator,
      phraseData.uuid,
      phraseData.timeSignature,
      phraseIndex,
    );
    // Seed the global cursor-toggle state on every cursor as it's created, so a
    // cursor born during gameplay/scroll honors the current toggle immediately
    // (and never flashes before the next SetCursorVisible event arrives).
    lessonCursor.setCursorVisibility(!this.cursorHidden);
    return lessonCursor;
  }

  // ---------------------------------------------------------------------------
  // Mobile camera scroll-follow (manager-driven; the cursor stays passive)
  // ---------------------------------------------------------------------------

  /** The first phrase's brace, if it's on screen and visible. The mobile
   *  camera follow uses this as the spatial reference for paddingLeft. */
  private getMobileFirstPhraseBrace(): Phaser.GameObjects.Image | undefined {
    if (!this.mobile || !this.mainCursor?.isAtLessonIteratorStart()) return undefined;
    const graphic =
      this.phrases.find((p) => p?.graphic._data.startTimestamp === 0)?.graphic ??
      this.phrases.find((p) => p !== undefined)?.graphic;
    if (!graphic?._data.showBrace) return undefined;
    const brace = graphic.brace;
    return brace && brace.visible ? brace : undefined;
  }

  /**
   * Mobile left-edge anchor for the CameraController: the brace's world-x
   * position and the *screen-pixel* gap that should be held between
   * viewport-left and the brace. The controller derives `paddingLeft` from
   * these each frame so the gap stays fixed across zoom changes.
   *
   * Returns undefined until the first-phrase brace exists and the shared
   * cursor is at the lesson's start (so the world position can be captured
   * cleanly). The scene retries each frame until this succeeds.
   */
  getMobileLeftAnchor(): { worldX: number; screenGap: number } | undefined {
    if (!this.mobile) return undefined;
    const brace = this.getMobileFirstPhraseBrace();
    if (!brace) return undefined;
    return { worldX: brace.x, screenGap: MOBILE_BRACE_VIEWPORT_GAP_PX };
  }

  /** Recompute the lesson zoom ceiling from the current layout. Moved here from
   *  the scene so it runs as part of onResize, right after the phrases are laid
   *  out — the scene then reads {@link maxZoomLevel} when applying its zoom. */
  updateMaxZoomLevel(paddingHorizontal: number, paddingVertical: number, braceWidth: number): void {
    const cam = this.scene.cameras.main;
    const vpWidth = cam.width;
    const vpHeight = cam.height;
    const extra = 5 * window.devicePixelRatio;
    const phraseWidth = vpWidth - (paddingHorizontal - braceWidth - extra) * 2;
    const phraseHeight = vpHeight - (paddingVertical - extra) * 2;
    this.maxZoomLevel = this.mobile
      ? vpHeight / phraseHeight
      : Math.min(vpWidth / phraseWidth, vpHeight / phraseHeight);
  }

  /** Mobile staff-sizing bounds (tighter horizontal padding for the scroll
   *  view). Mirrors what positionPhrase used to compute inline. */
  private mobileSizingProps(): { minProps: SizingProps; maxProps: SizingProps } {
    const maxProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 30,
      idealPhraseConfig: DefaultConfig({}),
      minPhraseConfig: MediumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 150,
      idealPaddingY: 75,
    };
    const minProps: SizingProps = {
      minPaddingX: 30,
      minPaddingY: 10,
      idealPhraseConfig: maxProps.minPhraseConfig,
      minPhraseConfig: MinimumConfig(),
      idealPaddingX: maxProps.minPaddingX,
      idealPaddingY: maxProps.minPaddingY,
      minStaffSpacingVertical: 0,
    };
    return { minProps, maxProps };
  }

  onResize({ width, height }: Dimens2D) {
    const minAspectRatio = SIGHT_READING_MIN_ASPECT_RATIO;
    const maxAspectRatio = SIGHT_READING_MAX_ASPECT_RATIO;
    // Compute the layout reference for the current mode and store it. Mobile
    // doesn't fire resize naturally, so the scene calls this manually at init
    // to give positionPhrase something to read from for the first phrase.
    const { minProps, maxProps } = this.mobile
      ? this.mobileSizingProps()
      : sightReadingDesktopSizing();
    const layout = optimizeStaffLineHeight(
      [width, height],
      { min: minProps, max: maxProps },
      minAspectRatio,
      maxAspectRatio,
      this.mobile ?? false,
    );
    this.lastLayout = layout;
    if (!layout.fit) {
      Logger.warn(
        '[PhraseManagerMuseflowRenderer::resize] not enough space for staff lines! Falling back to minimum configuration',
      );
    }

    // Mobile positions phrases as they're added (in positionPhrase); the only
    // thing onResize needs to do is store the reference layout above.
    if (this.mobile) return;

    const hasAnyPhrase = this.phrases.some((p) => p !== undefined);
    if (!hasAnyPhrase) return;

    const { paddingX, paddingY, phraseRenderingConfig, staffSpacingVertical, phraseDimens } =
      layout;
    const { topClefNumExtraLinesTop, innerHeight } = phraseDimens;
    const minWidth = minAspectRatio * innerHeight;
    const availableWidth = Math.max(minWidth, width - paddingX * 2);
    const dpr = window.devicePixelRatio;
    // Recompute the zoom ceiling from this layout before the scene re-applies zoom.
    const braceWidth = getBraceWidth(phraseDimens, this.scene) * dpr;
    this.updateMaxZoomLevel(paddingX * dpr, paddingY * dpr, braceWidth);
    const phraseHeightTotal =
      phraseDimens.topClefHeight.outerHeight +
      phraseDimens.bottomClefHeight.outerHeight +
      phraseRenderingConfig.clefGap +
      1 * phraseRenderingConfig.pitchLineGap;
    // Vertically center the stacked systems in the play area so extra vertical
    // space (e.g. fullscreen) splits evenly above and below the music instead of
    // all falling below a top-anchored staff. Falls back to paddingY as a floor
    // when the content is taller than the viewport. Mirrors the mobile (Horizontal)
    // path, which already centers via the camera view height.
    const numStackedPhrases = this.phrases.filter((p) => p !== undefined).length;
    const contentHeight =
      phraseHeightTotal * numStackedPhrases +
      staffSpacingVertical * Math.max(0, numStackedPhrases - 1);
    const verticalTopAnchor = Math.max(paddingY, (height - contentHeight) / 2);
    this.phrases.forEach((p) => {
      if (p === undefined) return;
      const yOffset = topClefNumExtraLinesTop * 0.5 * phraseRenderingConfig.pitchLineGap;
      const phraseY =
        verticalTopAnchor +
        yOffset +
        (p.graphic.bottom ? phraseHeightTotal + staffSpacingVertical : 0);
      p.graphic.resize(
        phraseRenderingConfig,
        availableWidth,
        paddingX,
        phraseY,
        phraseDimens,
        innerHeight,
      );
      const c = p.cursor;
      if (c === undefined) return;
      c.height = innerHeight * 1.25 * dpr;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      const y = (phraseY + innerHeight / 2) * dpr - c.height / 2;
      c.setY(y);
      if (!this.playbackStarted && c.idx === 0) {
        const firstNotePhrase =
          (this.activePhrase !== undefined ? this.phrases[this.activePhrase] : undefined) ??
          this.phrases.find((ph) => ph !== undefined);
        const firstNote = firstNotePhrase?.graphic.getFirstNotePosition();
        if (firstNote !== undefined) {
          // `lastNotePos` is the cursor's center (matches `it.left()` and the
          // lerp in `calcPos`). `setX` is the left edge, so subtract width/2 for
          // the visual placement. Storing `firstNote` (not `firstNote − w/2`)
          // in lastNotePos avoids a half-width jump when the first update runs.
          c.setLastNotePos(firstNote);
          c.setX(firstNote - c.width / 2);
        }
      }
    });
    this.keepFingerNumbersInView();
  }

  positionPhrase(phraseGraphic: PhraseGraphic, cursor?: CursorMobile): void {
    if (this.layoutConfig.layoutDirection === PhraseLayout.Horizontal) {
      const nextPhrasePosition = this.mostRecentAddedPhrase?.getNextPosition() ?? 0;
      // The layout reference is computed once per resize and reused for every
      // phrase added in this mode. If no resize has run yet, fall back to a
      // one-shot compute so we never read off a missing layout.
      if (!this.lastLayout) this.onResize(this.canvasDimens);
      if (!this.lastLayout) return;
      const { phraseDimens, phraseRenderingConfig } = this.lastLayout;
      const { innerHeight } = phraseDimens;
      const dpr = window.devicePixelRatio;
      const width = innerHeight * 4.5;
      phraseGraphic.resize(phraseRenderingConfig, width, 0, 0, phraseDimens, innerHeight);
      // Vertically center the phrase using its bounding box height and camera's view height,
      // since phraseDimens doesn't account for devicePixelRatio (DPR).
      const camHeight = this.getViewportSize().height;
      const centeredY = (camHeight - innerHeight * dpr) / 2;
      phraseGraphic.setY(centeredY);
      phraseGraphic.setX(nextPhrasePosition);
      if (this.fingeringTables !== undefined) {
        const view = this.rowsInView();
        phraseGraphic.keepFingerNumbersWithin(view.top, view.bottom);
      }
      if (cursor) {
        cursor.height = innerHeight * 1.25 * dpr;
        cursor.width = cursor.height / 10;
        cursor.radius = cursor.width / 2;
        // centeredY is already in canvas units (computed from cam.height), so
        // only innerHeight needs the dpr conversion. The desktop path differs:
        // there phraseY is CSS and the whole sum is scaled.
        const cursorY = centeredY + (innerHeight * dpr) / 2 - cursor.height / 2;
        cursor.setY(cursorY);
        const cursorX = phraseGraphic.getFirstNotePosition();
        cursor.setStartPosition(cursorX);
      }
    }
  }
}
